import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Collapse,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from '@mui/material';
import {
  Reply,
  MoreVert,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Info,
  Send,
  Schedule,
  Person,
  Build,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';

// Enhanced API service for conversational comments
const createEnhancedCommentService = (auth) => ({
  async getThreadedComments(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/threaded/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async replyToComment(commentId, reply, visibleToCustomer = true) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/comments/${commentId}/reply/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        reply,
        visible_to_customer: visibleToCustomer,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async markCommentResolved(commentId, resolutionComment = '') {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/comments/${commentId}/resolve/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        resolution_comment: resolutionComment,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async requestMoreInfo(commentId, infoRequest) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/comments/${commentId}/request-info/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        info_request: infoRequest,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async addRMAComment(rmaId, comment, commentType = 'general', visibleToCustomer = true) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/add/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        comment,
        comment_type: commentType,
        visible_to_customer: visibleToCustomer,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },
});

// Individual Comment Component
const CommentItem = ({ comment, isReply = false, isServiceman, onReply, onResolve, onRequestInfo, showActions = true }) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [infoRequestText, setInfoRequestText] = useState('');

  const getCommentTypeColor = (type) => {
    switch (type) {
      case 'reply': return 'primary';
      case 'info_request': return 'warning';
      case 'resolution': return 'success';
      case 'field_issue': return 'error';
      default: return 'default';
    }
  };

  const getCommentTypeLabel = (type) => {
    switch (type) {
      case 'reply': return 'Reply';
      case 'info_request': return 'Info Request';
      case 'resolution': return 'Resolution';
      case 'field_issue': return 'Field Issue';
      case 'status_update': return 'Status Update';
      default: return 'Comment';
    }
  };

  const isServicemanComment = comment.author_email && comment.author_email.includes('@company.com'); // Adjust based on your serviceman email pattern

  const handleReply = () => {
    if (replyText.trim()) {
      onReply(comment.id, replyText.trim());
      setReplyText('');
      setShowReplyBox(false);
    }
  };

  const handleResolve = () => {
    onResolve(comment.id, resolutionText.trim());
    setResolveDialog(false);
    setResolutionText('');
  };

  const handleRequestInfo = () => {
    onRequestInfo(comment.id, infoRequestText.trim());
    setInfoDialog(false);
    setInfoRequestText('');
  };

  return (
    <Box
      sx={{
        ml: isReply ? 4 : 0,
        mb: 1,
        p: 2,
        bgcolor: isReply ? 'grey.50' : 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        borderLeft: isReply ? '4px solid' : '1px solid',
        borderLeftColor: isReply ? 'primary.main' : 'grey.200',
      }}
    >
      {/* Comment Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: isServicemanComment ? 'secondary.main' : 'primary.main' }}>
            {isServicemanComment ? <Build /> : <Person />}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">
              {comment.author || 'Unknown User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(comment.created_at).toLocaleString()}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={getCommentTypeLabel(comment.comment_type)}
            color={getCommentTypeColor(comment.comment_type)}
            variant="outlined"
          />
          {comment.is_resolved && (
            <Chip
              size="small"
              label="Resolved"
              color="success"
              icon={<CheckCircle />}
            />
          )}
          {comment.requires_customer_response && (
            <Chip
              size="small"
              label="Awaiting Response"
              color="warning"
              icon={<Schedule />}
            />
          )}
        </Box>

        {showActions && (
          <IconButton
            size="small"
            onClick={(e) => setActionMenuAnchor(e.currentTarget)}
          >
            <MoreVert />
          </IconButton>
        )}
      </Box>

      {/* Comment Content */}
      <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-word' }}>
        {comment.comment}
      </Typography>

      {/* Action Buttons */}
      {showActions && !comment.is_resolved && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Reply />}
            onClick={() => setShowReplyBox(!showReplyBox)}
          >
            Reply
          </Button>
        </Box>
      )}

      {/* Reply Input Box */}
      <Collapse in={showReplyBox}>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleReply}
              disabled={!replyText.trim()}
              startIcon={<Send />}
            >
              Send Reply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowReplyBox(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Collapse>

      {/* Action Menu (for servicemen) */}
      {isServiceman && (
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={() => setActionMenuAnchor(null)}
        >
          <MenuItem onClick={() => {
            setResolveDialog(true);
            setActionMenuAnchor(null);
          }}>
            <CheckCircle sx={{ mr: 1 }} />
            Mark as Resolved
          </MenuItem>
          <MenuItem onClick={() => {
            setInfoDialog(true);
            setActionMenuAnchor(null);
          }}>
            <Info sx={{ mr: 1 }} />
            Request More Info
          </MenuItem>
        </Menu>
      )}

      {/* Resolve Dialog */}
      <Dialog open={resolveDialog} onClose={() => setResolveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Comment as Resolved</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Resolution Comment (Optional)"
            value={resolutionText}
            onChange={(e) => setResolutionText(e.target.value)}
            placeholder="Add any notes about how this was resolved..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(false)}>Cancel</Button>
          <Button onClick={handleResolve} variant="contained">
            Mark Resolved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={infoDialog} onClose={() => setInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request More Information</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Information Request"
            value={infoRequestText}
            onChange={(e) => setInfoRequestText(e.target.value)}
            placeholder="What additional information do you need from the customer?"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRequestInfo}
            variant="contained"
            disabled={!infoRequestText.trim()}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Comment Thread Component
const CommentThread = ({ thread, isServiceman, onReply, onResolve, onRequestInfo }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Thread Header */}
        {thread.replies.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Conversation Thread
              </Typography>
              <Badge badgeContent={thread.reply_count} color="primary">
                <Reply />
              </Badge>
              {thread.has_unresolved && (
                <Chip size="small" label="Unresolved" color="warning" />
              )}
              {thread.requires_response && (
                <Chip size="small" label="Awaiting Response" color="error" />
              )}
            </Box>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        )}

        {/* Root Comment */}
        <CommentItem
          comment={thread.root_comment}
          isServiceman={isServiceman}
          onReply={onReply}
          onResolve={onResolve}
          onRequestInfo={onRequestInfo}
        />

        {/* Replies */}
        <Collapse in={expanded}>
          {thread.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              isReply={true}
              isServiceman={isServiceman}
              onReply={onReply}
              onResolve={onResolve}
              onRequestInfo={onRequestInfo}
            />
          ))}
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Main Enhanced Comment System Component
const EnhancedCommentSystem = ({ rmaId, isServiceman, onNotification }) => {
  const auth = useAuth();
  const [threads, setThreads] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleToCustomer, setVisibleToCustomer] = useState(true);
  const scrollEndRef = useRef(null);
  const newCommentRef = useRef(null);

  const commentService = createEnhancedCommentService(auth);

  const loadComments = async () => {
    try {
      setLoading(true);
      const data = await commentService.getThreadedComments(rmaId);
      setThreads(data.threads || []);
    } catch (error) {
      onNotification(`Error loading comments: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await commentService.addRMAComment(rmaId, newComment, 'general', visibleToCustomer);
      setNewComment('');
      await loadComments();
      onNotification('Comment added successfully', 'success');
      scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      onNotification('Error adding comment', 'error');
    }
  };

  const handleReply = async (commentId, reply) => {
    try {
      await commentService.replyToComment(commentId, reply, visibleToCustomer);
      await loadComments();
      onNotification('Reply added successfully', 'success');
      scrollEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      onNotification('Error adding reply', 'error');
    }
  };

  const handleResolve = async (commentId, resolutionComment) => {
    try {
      await commentService.markCommentResolved(commentId, resolutionComment);
      await loadComments();
      onNotification('Comment marked as resolved', 'success');
    } catch (error) {
      onNotification('Error resolving comment', 'error');
    }
  };

  const handleRequestInfo = async (commentId, infoRequest) => {
    try {
      await commentService.requestMoreInfo(commentId, infoRequest);
      await loadComments();
      onNotification('Information request sent', 'success');
    } catch (error) {
      onNotification('Error sending information request', 'error');
    }
  };

  useEffect(() => {
    if (rmaId) {
      loadComments();
    }
  }, [rmaId]);

  const unresolvedThreads = threads.filter(thread => thread.has_unresolved);
  const resolvedThreads = threads.filter(thread => !thread.has_unresolved);
  const awaitingResponseThreads = threads.filter(thread => thread.requires_response);

  return (
    <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<Reply />}
          label={`${threads.length} Conversations`}
          color="primary"
          variant="outlined"
        />
        {unresolvedThreads.length > 0 && (
          <Chip
            icon={<Schedule />}
            label={`${unresolvedThreads.length} Unresolved`}
            color="warning"
          />
        )}
        {awaitingResponseThreads.length > 0 && (
          <Chip
            icon={<Info />}
            label={`${awaitingResponseThreads.length} Awaiting Response`}
            color="error"
          />
        )}
      </Box>

      {/* Comments List with Scroll - Takes up available space */}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        mb: 2,
        pr: 1,
        minHeight: 0 // This is important for proper flex behavior
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography color="text.secondary">Loading comments...</Typography>
          </Box>
        ) : threads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No comments yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start the conversation by adding the first comment below.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Unresolved Threads First */}
            {unresolvedThreads.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="warning.main" gutterBottom>
                  Unresolved Conversations ({unresolvedThreads.length})
                </Typography>
                {unresolvedThreads.map((thread) => (
                  <CommentThread
                    key={thread.id}
                    thread={thread}
                    isServiceman={isServiceman}
                    onReply={handleReply}
                    onResolve={handleResolve}
                    onRequestInfo={handleRequestInfo}
                  />
                ))}
              </Box>
            )}

            {/* Awaiting Response Threads */}
            {awaitingResponseThreads.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Awaiting Customer Response ({awaitingResponseThreads.length})
                </Typography>
                {awaitingResponseThreads
                  .filter(thread => !unresolvedThreads.includes(thread))
                  .map((thread) => (
                    <CommentThread
                      key={thread.id}
                      thread={thread}
                      isServiceman={isServiceman}
                      onReply={handleReply}
                      onResolve={handleResolve}
                      onRequestInfo={handleRequestInfo}
                    />
                  ))}
              </Box>
            )}

            {/* Resolved Threads */}
            {resolvedThreads.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Resolved Conversations ({resolvedThreads.length})
                </Typography>
                {resolvedThreads.map((thread) => (
                  <CommentThread
                    key={thread.id}
                    thread={thread}
                    isServiceman={isServiceman}
                    onReply={handleReply}
                    onResolve={handleResolve}
                    onRequestInfo={handleRequestInfo}
                  />
                ))}
              </Box>
            )}
          </>
        )}
        <div ref={scrollEndRef} />
      </Box>

      {/* New Comment Section - Fixed at Bottom */}
      <Card
        ref={newCommentRef}
        sx={{
          flexShrink: 0, // Prevents shrinking
          bgcolor: 'background.paper',
          boxShadow: 3 // Add shadow to make it more prominent
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add New Comment
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Start a new conversation..."
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={handleAddComment}
              disabled={!newComment.trim() || loading}
            >
              Post Comment
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnhancedCommentSystem;